import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate } from 'react-router-dom';
import { fetchItemsWithCache } from '../../utils/apiCache';
import { getAdminSession } from '../../utils/adminSession';
import { getAdminAuthHeaders } from '../../utils/adminAuth';
import { CACHE_KEYS, entriesUrl, startNewSeasonUrl } from '../../utils/apiEndpoints';

const PageContainer = styled.div`
  max-width: 780px;
  margin: 4rem auto;
  padding: 2rem;
  background-color: var(--gray-900);
  color: var(--gray-50);
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
`;

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const WarningCard = styled.div`
  margin: 1.5rem 0;
  padding: 1rem 1.25rem;
  border-radius: 10px;
  background: rgba(214, 85, 0, 0.12);
  border: 1px solid rgba(214, 85, 0, 0.5);
  color: #ffd8ae;
`;

const DetailText = styled.p`
  margin: 0;
  line-height: 1.6;
  color: var(--gray);
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 1.25rem;
`;

const HelperText = styled.span`
  font-size: 0.875rem;
  color: var(--gray);
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1.5rem;
`;

const PrimaryButton = styled.button`
  padding: 0.8rem 1.25rem;
  border: none;
  border-radius: 8px;
  background: var(--primary);
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const LinkButton = styled(Link)`
  padding: 0.8rem 1.25rem;
  border-radius: 8px;
  border: 1px solid #555;
  background: transparent;
  color: var(--gray-50);
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const ResultBox = styled.div<{ $success: boolean }>`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 8px;
  background: ${({ $success }) => ($success ? '#12331a' : '#3a1010')};
  color: ${({ $success }) => ($success ? '#c9f7d6' : '#ffd1d1')};
`;

type PlayerRecord = {
  name: string;
  position?: string;
  number?: number | string;
  [key: string]: unknown;
};

type ResetResponse = {
  success: boolean;
  summary?: {
    requested?: number;
    archived?: number;
    skipped?: number;
    notFound?: number;
    errors?: number;
  };
  details?: {
    archived?: string[];
    skipped?: string[];
    errors?: Array<{ player: string; error: string }>;
  };
  message?: string;
};

const StartNewSeason: React.FC = () => {
  const [players, setPlayers] = useState<PlayerRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ResetResponse | null>(null);
  const [success, setSuccess] = useState(false);

  const session = getAdminSession();

  useEffect(() => {
    const loadPlayers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        localStorage.removeItem(CACHE_KEYS.players);

        const items = await fetchItemsWithCache<PlayerRecord>(
          entriesUrl('tigers_players'),
          CACHE_KEYS.players,
          5 * 60 * 1000
        );

        setPlayers(items ?? []);
      } catch (loadError) {
        const message = loadError instanceof Error ? loadError.message : 'Unable to load the current roster.';
        setError(message);
      } finally {
        setIsLoading(false);
      }
    };

    loadPlayers();
  }, []);

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  const handleReset = async () => {
    if (players.length === 0) {
      setError('There are no players in the current roster to archive.');
      return;
    }

    if (!window.confirm(`Archive all ${players.length} player(s) to the previous-season roster and remove them from the active squad?`)) {
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setResult(null);
      setSuccess(false);

      const response = await fetch(startNewSeasonUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAdminAuthHeaders(session),
        },
        body: JSON.stringify({
          teamName: 'tigers',
        }),
      });

      const data = (await response.json()) as ResetResponse;
      setResult(data);

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Unable to start the new season reset.');
      }

      setSuccess(true);
      setPlayers([]);
      localStorage.removeItem(CACHE_KEYS.players);
      localStorage.removeItem(CACHE_KEYS.playersArchive);
    } catch (resetError) {
      const message = resetError instanceof Error ? resetError.message : 'Unable to start the new season reset.';
      setSuccess(false);
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Title>Start New Season</Title>

      <WarningCard>
        Warning: this archives the current roster into the previous-season player list and removes those players from the active squad so the next season can start cleanly.
      </WarningCard>

      <DetailText>
        All players currently on the roster will be moved to the archive, keeping their profiles available to re-sign from the add-player form.
      </DetailText>

      <FieldGroup>
        <HelperText>Current roster ({players.length} player(s))</HelperText>
        <HelperText>Every player in the current roster will be archived.</HelperText>
      </FieldGroup>

      <HelperText style={{ display: 'block', marginTop: '0.75rem' }}>
        Use the add-player form after this to re-sign archived players or add fresh ones.
      </HelperText>

      {error && !result ? <ResultBox $success={false}>{error}</ResultBox> : null}
      {result ? (
        <ResultBox $success={success}>
          {success ? result.message || 'Season reset complete.' : error || result.message}
          {result.summary ? (
            <div style={{ marginTop: '0.5rem' }}>
              Archived: {result.summary.archived ?? 0} · Skipped: {result.summary.skipped ?? 0}
            </div>
          ) : null}
          {result.details?.errors?.length ? (
            <ul>
              {result.details.errors.map(({ player, error: playerError }) => (
                <li key={player}>{player}: {playerError}</li>
              ))}
            </ul>
          ) : null}
        </ResultBox>
      ) : null}

      <ActionsRow>
        <PrimaryButton type="button" onClick={handleReset} disabled={isLoading || isSubmitting || players.length === 0}>
          {isSubmitting ? 'Starting season...' : 'Archive and reset roster'}
        </PrimaryButton>
        <LinkButton to="/admin">Back to admin</LinkButton>
      </ActionsRow>
    </PageContainer>
  );
};

export default StartNewSeason;
